import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { useSeoMeta } from '@unhead/react';
import { ArrowLeft, Upload, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useUploadFile } from '@/hooks/useUploadFile';
import { useToast } from '@/hooks/useToast';
import { LoginArea } from '@/components/auth/LoginArea';
import { APP_LISTING_KIND, CATEGORIES, createAppTags } from '@/lib/appTypes';

export default function AddApp() {
  useSeoMeta({
    title: 'Add App - NostrStore.com',
    description: 'Submit your app to the decentralized Nostr marketplace',
  });

  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { mutateAsync: createEvent } = useNostrPublish();
  const { mutateAsync: uploadFile, isPending: isUploading } = useUploadFile();
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    url: '',
    category: '',
  });

  const [isCreator, setIsCreator] = useState(true);
  const [creatorNpub, setCreatorNpub] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: 'Authentication Required',
        description: 'Please log in to submit an app',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.name || !formData.description || !formData.url || !formData.category) {
      toast({
        title: 'Missing Fields',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    // Validate creator npub if provided
    let creatorPubkey: string | undefined;
    if (!isCreator && creatorNpub) {
      try {
        const decoded = nip19.decode(creatorNpub.trim());
        if (decoded.type !== 'npub') {
          toast({
            title: 'Invalid Creator',
            description: 'Please enter a valid npub address',
            variant: 'destructive',
          });
          return;
        }
        creatorPubkey = decoded.data;
      } catch {
        toast({
          title: 'Invalid Creator',
          description: 'Please enter a valid npub address',
          variant: 'destructive',
        });
        return;
      }
    }

    setIsSubmitting(true);

    try {
      // Upload image and logo if provided
      let imageUrl: string | undefined;
      let logoUrl: string | undefined;

      if (imageFile) {
        const [[_, url]] = await uploadFile(imageFile);
        imageUrl = url;
      }

      if (logoFile) {
        const [[_, url]] = await uploadFile(logoFile);
        logoUrl = url;
      }

      // Generate a unique ID for the app
      const id = formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '-' + Date.now();

      // Create tags for the event
      const tags = createAppTags({
        id,
        name: formData.name,
        description: formData.description,
        url: formData.url,
        category: formData.category,
        image: imageUrl,
        logo: logoUrl,
        creator: creatorPubkey,
      });

      // Publish the event
      await createEvent({
        kind: APP_LISTING_KIND,
        content: '',
        tags,
      });

      toast({
        title: 'Success!',
        description: 'Your app has been published to the marketplace',
      });

      // Navigate to the app's detail page
      const naddr = nip19.naddrEncode({
        kind: APP_LISTING_KIND,
        pubkey: user.pubkey,
        identifier: id,
      });

      navigate(`/${naddr}`);
    } catch (error) {
      console.error('Error publishing app:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to publish app',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-black/95 relative px-4 py-8">
        {/* Background Effects */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-purple-800/10 via-purple-900/5 to-transparent opacity-50" />
        </div>

        <div className="relative max-w-2xl mx-auto">
          <Card className="bg-gradient-to-br from-black/40 via-purple-500/5 to-purple-900/10 border-purple-500/20 backdrop-blur-md">
            <CardHeader>
            <CardTitle className="text-2xl bg-gradient-to-r from-purple-200 to-purple-100 bg-clip-text text-transparent">Login Required</CardTitle>
            <CardDescription className="text-purple-300/70">
              Please log in with your Nostr account to submit an app
            </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col items-center gap-6">
              <LoginArea className="w-full max-w-sm" />
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="border-purple-500/20 text-purple-200 hover:bg-purple-500/10"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Marketplace
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black/95 relative px-4 py-8">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-purple-800/10 via-purple-900/5 to-transparent opacity-50" />
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-[40rem] h-[40rem] bg-purple-600/5 rounded-full mix-blend-normal filter blur-[100px] opacity-70 animate-blob" style={{ animationDuration: '20s' }} />
          <div className="absolute top-[20%] -right-4 w-[35rem] h-[35rem] bg-purple-800/5 rounded-full mix-blend-normal filter blur-[100px] opacity-70 animate-blob" style={{ animationDuration: '25s', animationDelay: '2s' }} />
        </div>
      </div>

      <div className="relative max-w-3xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6 text-purple-200 hover:text-purple-100 hover:bg-purple-500/10"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Marketplace
        </Button>

        <Card className="bg-gradient-to-br from-black/40 via-purple-500/5 to-purple-900/10 border-purple-500/20 backdrop-blur-md">
          <CardHeader>
            <CardTitle className="text-3xl bg-gradient-to-r from-purple-200 to-purple-100 bg-clip-text text-transparent">
              Submit Your App
            </CardTitle>
            <CardDescription className="text-purple-300/70">
              Share your app with the decentralized community
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* App Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-purple-100">
                  App Name *
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="My Awesome App"
                  required
                  className="bg-black/30 border-purple-500/20 text-purple-50 placeholder:text-purple-300/30 focus:border-purple-500/40"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description" className="text-purple-100">
                  Description *
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe what your app does..."
                  rows={4}
                  required
                  className="bg-black/30 border-purple-500/20 text-purple-50 placeholder:text-purple-300/30 focus:border-purple-500/40 resize-none"
                />
              </div>

              {/* URL */}
              <div className="space-y-2">
                <Label htmlFor="url" className="text-purple-100">
                  App URL *
                </Label>
                <Input
                  id="url"
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://myapp.com"
                  required
                  className="bg-black/30 border-purple-500/20 text-purple-50 placeholder:text-purple-300/30 focus:border-purple-500/40"
                />
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="category" className="text-purple-100">
                  Category *
                </Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger className="bg-black/30 border-purple-500/20 text-purple-50 focus:border-purple-500/40">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent className="bg-black/95 border-purple-500/20 backdrop-blur-xl">
                    {CATEGORIES.map((category) => (
                      <SelectItem
                        key={category}
                        value={category}
                        className="text-purple-100 focus:bg-purple-500/20 focus:text-purple-50"
                      >
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Creator Selection */}
              <div className="space-y-4 p-4 rounded-lg border border-purple-500/20 bg-purple-900/10">
                <Label className="text-purple-100 text-base font-semibold">
                  Are you the creator of this app? *
                </Label>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setIsCreator(true)}
                    className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                      isCreator
                        ? 'border-purple-500 bg-purple-500/20 text-purple-100'
                        : 'border-purple-500/20 bg-black/20 text-purple-300 hover:border-purple-500/40'
                    }`}
                  >
                    <div className="font-medium">Yes, I'm the creator</div>
                    <div className="text-xs opacity-70 mt-1">You will be listed as the creator</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsCreator(false)}
                    className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                      !isCreator
                        ? 'border-purple-500 bg-purple-500/20 text-purple-100'
                        : 'border-purple-500/20 bg-black/20 text-purple-300 hover:border-purple-500/40'
                    }`}
                  >
                    <div className="font-medium">No, posting for someone else</div>
                    <div className="text-xs opacity-70 mt-1">Provide creator's npub below</div>
                  </button>
                </div>

                {!isCreator && (
                  <div className="space-y-2 pt-2">
                    <Label htmlFor="creator-npub" className="text-purple-100">
                      Creator's npub *
                    </Label>
                    <Input
                      id="creator-npub"
                      value={creatorNpub}
                      onChange={(e) => setCreatorNpub(e.target.value)}
                      placeholder="npub1..."
                      required={!isCreator}
                      className="bg-black/30 border-purple-500/20 text-purple-50 placeholder:text-purple-300/30 focus:border-purple-500/40 font-mono text-sm"
                    />
                    <p className="text-xs text-purple-300/60">
                      The app will show a warning that it was not posted by the creator.
                    </p>
                  </div>
                )}
              </div>

              {/* App Screenshot/Image */}
              <div className="space-y-2">
                <Label htmlFor="image" className="text-purple-100">
                  App Screenshot
                </Label>
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <Input
                      id="image"
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="bg-black/30 border-purple-500/20 text-purple-50 file:bg-purple-600/20 file:text-purple-200 file:border-0 file:mr-4 file:py-2 file:px-4 file:rounded-md hover:file:bg-purple-600/30"
                    />
                  </div>
                  {imagePreview && (
                    <div className="relative w-full h-48 rounded-lg overflow-hidden border border-purple-500/20">
                      <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
              </div>

              {/* App Logo */}
              <div className="space-y-2">
                <Label htmlFor="logo" className="text-purple-100">
                  App Logo/Icon
                </Label>
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <Input
                      id="logo"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="bg-black/30 border-purple-500/20 text-purple-50 file:bg-purple-600/20 file:text-purple-200 file:border-0 file:mr-4 file:py-2 file:px-4 file:rounded-md hover:file:bg-purple-600/30"
                    />
                  </div>
                  {logoPreview && (
                    <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-purple-500/20">
                      <img src={logoPreview} alt="Logo Preview" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  disabled={isSubmitting || isUploading}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 text-purple-50 border-0 shadow-lg shadow-purple-500/30 hover:shadow-purple-500/40 transition-all"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Publishing...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Publish App
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
